import React, { useState } from 'react';
import { Link } from 'react-router-dom';
// jspdf loaded dynamically in downloadPdf - see the note in App.js. This modal
// renders on /study-guide and the exam results screen, so a static import here
// would put the PDF library in the main bundle for every visitor.
import { generateLeadMagnet } from './utils/generateLeadMagnet';
import { safeSetItem } from './safeStorage';
import { track } from './analytics';

export default function LeadMagnetModal({ isOpen, onClose, source = 'study-guide-lead-magnet' }) {
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    // Honeypot: hidden from people, filled by bots. See subscribe.js.
    const [honeypot, setHoneypot] = useState('');

    if (!isOpen) return null;

    const downloadPdf = async () => {
        const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
            import('jspdf'),
            import('jspdf-autotable'),
        ]);
        const doc = new jsPDF();
        generateLeadMagnet(doc, autoTable);
        doc.save('Life-in-the-UK-Free-Cheat-Sheet.pdf');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setError('');
        setSubmitting(true);
        try {
            // Own endpoint, not Formspree — see netlify/functions/subscribe.js
            // for why. It validates and de-duplicates server-side.
            const res = await fetch('/.netlify/functions/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, source, website: honeypot }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setSubmitted(true);
                // A repeat download is not a new lead; counting it would
                // overstate captures in GA4 against the stored list.
                if (!data.duplicate) track('lead_magnet_submit');
                safeSetItem('leadMagnetDownloaded', 'true');
                // Deliberately not awaited into the surrounding try: the
                // address is already saved and the success state is shown, so
                // a PDF failure must not be reported as "Network error" and
                // must not undo the capture. Its own catch surfaces it.
                downloadPdf().catch(() => {
                    setError('Saved your email, but the download failed. Open /free-cheat-sheet to view it.');
                });
            } else {
                setError(data.error || 'Something went wrong. Please try again.');
            }
        } catch {
            setError('Network error. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-ink-50 rounded-2xl max-w-md w-full p-6 shadow-lift-3">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-ink-900">📥 Free Cheat Sheet</h3>
                    <button onClick={onClose} className="text-ink-500 hover:text-ink-800 text-2xl leading-none u-tap-halo">&times;</button>
                </div>
                {submitted ? (
                    <div className="text-center py-6">
                        <div className="text-4xl mb-3">✅</div>
                        <h4 className="text-lg font-bold text-ink-900 mb-2">Downloaded!</h4>
                        <p className="text-sm text-ink-600">Your free cheat sheet should be downloading now. Check your downloads folder.</p>
                        <Link
                            to="/free-cheat-sheet"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={onClose}
                            className="mt-6 inline-flex items-center justify-center bg-action-600 hover:bg-action-700 text-white min-h-tap px-6 rounded-xl font-bold transition"
                        >
                            📖 Open Shareable Web Version
                        </Link>
                        <button onClick={onClose} className="mt-3 block mx-auto text-sm text-ink-500 hover:text-ink-800 transition min-h-tap">Close</button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <p className="text-sm text-ink-600">
                            Get the free 1-page Key Dates &amp; Patron Saints cheat sheet: handbook-verified, downloadable, print-ready.
                        </p>
                        <div>
                            <label className="block text-sm font-medium text-ink-700 mb-1">Email Address *</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                className="w-full border border-ink-300 bg-ink-50 text-ink-900 rounded-lg px-3 min-h-tap text-base focus:ring-2 focus:ring-action-500 focus:border-action-500 outline-none"
                            />
                        </div>
                        {/* Honeypot. Hidden from sighted users and from screen
                            readers, and taken out of the tab order, so only a
                            bot filling every field will populate it. */}
                        <div className="hidden" aria-hidden="true">
                            <label htmlFor="website">Leave this field empty</label>
                            <input
                                type="text"
                                id="website"
                                name="website"
                                tabIndex={-1}
                                autoComplete="off"
                                value={honeypot}
                                onChange={(e) => setHoneypot(e.target.value)}
                            />
                        </div>
                        {error && <p className="text-negative-600 text-xs">{error}</p>}
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-action-600 hover:bg-action-700 disabled:opacity-60 disabled:cursor-not-allowed text-white min-h-tap rounded-xl font-bold transition shadow-lift-2"
                        >
                            {submitting ? 'Checking…' : 'Get My Free Cheat Sheet'}
                        </button>
                        <p className="text-xs text-ink-500 text-center">No spam. Just your free PDF and occasional study tips.</p>
                    </form>
                )}
            </div>
        </div>
    );
}
